/**
 * useRoleLevel — Shared hook for role-based UI gating.
 *
 * Reads the role from the DB-canonical /api/me response (Phase 19.S frontend
 * migration, May 6, 2026) and maps it to a numeric level so pages can write
 * `if (roleLevel >= 4) show Trainer UI`.
 *
 * Why /api/me, not the JWT claim? The Auth0 token claim is baked in at
 * token-issue time and only refreshes via re-login. Phase 19.S made the DB
 * the source of truth for backend authorization; this hook brings the
 * frontend in line so role changes (promotion, demotion, deactivation)
 * propagate without a forced logout.
 *
 * The hook still degrades gracefully on first paint: while /api/me is
 * loading, `role === ''` and `level === 0`, so role-gated UI hides until
 * the fetch resolves. TanStack Query caches /api/me for 5 minutes, so
 * subsequent renders are synchronous.
 *
 * Matches the hierarchy from apps/api/src/config/auth.config.ts and the
 * DashboardLayout's nav filtering.
 *
 * Extracted in Phase 23.2.7 — was previously duplicated across
 * ProposalDetailPage, SOPDetailPage, DashboardPage, AssessmentDetailPage.
 * Existing duplicates left in place to avoid drive-by edits outside this
 * chunk's scope; new pages should prefer this hook.
 */

import { useCurrentUser } from '../../hooks/useUsers';

export const ROLE_LEVELS: Record<string, number> = {
  super_admin: 6,
  admin: 5,
  trainer: 4,
  manager: 3,
  operator: 1,
};

export interface RoleLevelInfo {
  /** The raw role string from the DB (e.g., 'trainer'). Empty string while
   *  /api/me is loading or the user has no DB record yet. */
  role: string;
  /** Numeric level per the ROLE_LEVELS map; 0 if unknown / loading. */
  level: number;
  /** Shorthand: trainer (4) or higher — authors/editors. */
  isTrainerOrHigher: boolean;
  /** Shorthand: manager (3) — explicitly a manager, not a trainer/admin. */
  isManager: boolean;
}

export function useRoleLevel(): RoleLevelInfo {
  const { data: currentUser } = useCurrentUser();
  const role = (currentUser?.role ?? '').trim();
  const level = role ? (ROLE_LEVELS[role] ?? 0) : 0;
  return {
    role,
    level,
    isTrainerOrHigher: level >= 4,
    isManager: level === 3,
  };
}
